import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface COAStatusResult {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'not_a_coa';
  message: string | null;
  coa_fields_extracted: string[] | null;
  coa_strain: string | null;
  listing_strain: string | null;
  extraction_cost_usd: number | null;
  updated_listing: Record<string, unknown> | null;
}

interface UseCOAProgressReturn {
  result: COAStatusResult | null;
  isComplete: boolean;
  isError: boolean;
  reset: () => void;
}

const POLL_INTERVAL_MS = 4000;
const MAX_CONSECUTIVE_ERRORS = 5;

export function useCOAProgress(jobId: string | null): UseCOAProgressReturn {
  const [result, setResult] = useState<COAStatusResult | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isError, setIsError] = useState(false);
  const consecutiveErrorsRef = useRef(0);

  const reset = useCallback(() => {
    setResult(null);
    setIsComplete(false);
    setIsError(false);
    consecutiveErrorsRef.current = 0;
  }, []);

  useEffect(() => {
    if (!jobId) return;

    let stopped = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const fetchStatus = async () => {
      if (stopped) return;
      try {
        const { data, error } = await supabase.functions.invoke('get-coa-status', {
          body: { job_id: jobId },
        });

        if (error) {
          consecutiveErrorsRef.current++;
          console.warn(`COA status poll error (${consecutiveErrorsRef.current}/${MAX_CONSECUTIVE_ERRORS}):`, error);
          if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
            setIsComplete(true);
            setIsError(true);
            setResult({
              job_id: jobId,
              status: 'failed',
              message: 'Lost connection to COA processor. Please check back or retry.',
              coa_fields_extracted: null, coa_strain: null, listing_strain: null,
              extraction_cost_usd: null, updated_listing: null,
            });
            return; // stop polling
          }
        } else if (data) {
          consecutiveErrorsRef.current = 0;
          setResult(data as COAStatusResult);

          const status = data.status;
          if (status === 'completed' || status === 'not_a_coa') {
            setIsComplete(true);
            return; // stop polling
          } else if (status === 'failed') {
            setIsComplete(true);
            setIsError(true);
            return; // stop polling
          }
        }
      } catch (err) {
        consecutiveErrorsRef.current++;
        console.warn('COA status fetch exception:', err);
        if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
          setIsComplete(true);
          setIsError(true);
          return;
        }
      }

      // Schedule next poll only if not stopped/complete
      if (!stopped) {
        timeoutId = setTimeout(fetchStatus, POLL_INTERVAL_MS);
      }
    };

    // Initial fetch after a short delay
    timeoutId = setTimeout(fetchStatus, 1000);

    return () => {
      stopped = true;
      clearTimeout(timeoutId);
    };
  }, [jobId]);

  return { result, isComplete, isError, reset };
}
